import * as React from 'react';
import styled from 'styled-components';
import Switcher from '@sdk/components/forms/Switcher';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { TimeOffType, TypeTimeOff } from '@sdk/timeOffs/types/TimeOffType';

import type { SetTimeOffTypes } from '../../types';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';

const TIME_OFF_TYPE_INPUT_WIDTH = 123;

const Container = styled.div`
  width: ${TIME_OFF_TYPE_INPUT_WIDTH}px;
  margin-right: 10px;
`;

const StyledRow = styled(Row)`
  align-items: center;
`;

const TimeOfTypeRow = styled(RowSpaceBetween)<{ withInput: boolean }>`
  margin-bottom: 20px;
  padding-top: ${({ withInput }): number => (withInput ? 0 : 3)}px;
  padding-bottom: ${({ withInput }): number => (withInput ? 0 : 3)}px;
`;

type Props = {
  timeOffTypes: TimeOffType[];
  useGlobalLeaveCap: boolean;
  onTimeOffTypesChange: SetTimeOffTypes;
};

const TimeOffTypes: React.FC<React.PropsWithChildren<Props>> = ({
  timeOffTypes,
  useGlobalLeaveCap,
  onTimeOffTypesChange,
}) => {
  const { currentUser } = useAuth();

  const parseCapValue = (value: string | number): any => {
    if (value === 0) {
      return null;
    }
    const intCap = Number(value);
    value = Number.isNaN(intCap) ? null : intCap;

    return value;
  };

  const onTimeOffTypeEnabledChange = (value: boolean, itemType: TypeTimeOff): void => {
    onTimeOffTypesChange(
      timeOffTypes.map((item) => (item.type === itemType ? { ...item, enabled: value } : item))
    );
  };

  const onCapChange = (value: string, itemType: TypeTimeOff): void => {
    onTimeOffTypesChange(
      timeOffTypes.map((item) =>
        item.type === itemType ? { ...item, cap: parseCapValue(value) } : item
      )
    );
  };

  const shouldDisableTimeOffType = (timeOffType: TimeOffType): boolean => {
    const enabledTypes = timeOffTypes.filter((t) => t.enabled);
    return timeOffType.enabled && enabledTypes.length === 1;
  };

  return (
    <SettingsSection>
      <Text14 semiBold themeColor={ThemeColorType.gray}>
        {gettext('Time off types')}
      </Text14>
      <Text14 themeColor={ThemeColorType.gray} pBottom={20}>
        {gettext('Enable or disable the time off types you use:')}
      </Text14>

      <Column>
        {timeOffTypes.map((timeOffType) => (
          <TimeOfTypeRow key={timeOffType.id} withInput={timeOffType.enabled && useGlobalLeaveCap}>
            <Text14 themeColor={ThemeColorType.gray}>{timeOffType.name}</Text14>
            <StyledRow>
              {timeOffType.enabled && !useGlobalLeaveCap && (
                <>
                  <Text14 pRight={10} themeColor={ThemeColorType.gray}>
                    {gettext('Total per year:')}
                  </Text14>
                  <Container>
                    <TextField
                      placeholder={gettext('Unlimited')}
                      postfix={gettext('Days')}
                      value={timeOffType.cap || ''}
                      onChange={(e): void => onCapChange(e.currentTarget.value, timeOffType.type)}
                      disabled={!currentUser.isAdmin || !currentUser.org.isPremium}
                    />
                  </Container>
                </>
              )}
              <Switcher
                value={timeOffType.enabled}
                onChange={(value: boolean): void =>
                  onTimeOffTypeEnabledChange(value, timeOffType.type)
                }
                disabled={
                  shouldDisableTimeOffType(timeOffType) ||
                  !currentUser.isAdmin ||
                  !currentUser.org.isPremium
                }
                small
              />
            </StyledRow>
          </TimeOfTypeRow>
        ))}
      </Column>
    </SettingsSection>
  );
};

export default TimeOffTypes;
